import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { itemStaggerAnimation } from '@examdojo/animation';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { applySuperLatexActions, SuperLatexComponent } from '@examdojo/super-latex';
import { KatexOptions, MarkdownModule } from 'ngx-markdown';
import { ContentBlockType } from '../model';
import { applyLearningContentActions } from './learning-content-action.util';
import { Solver } from './solver.model';

@Component({
  selector: 'dojo-solver',
  imports: [SuperLatexComponent, MatExpansionModule, MarkdownModule, ButtonComponent, IconComponent],
  templateUrl: './solver.component.html',
  styleUrl: './solver.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [itemStaggerAnimation()],
})
export class SolverComponent {
  readonly solver = input.required<Solver>();
  readonly expanded = signal(true);
  readonly katexOptions: KatexOptions = {
    throwOnError: false,
    strict: 'warn',
  };

  readonly slidesViewModel = computed(() => {
    return this.solver().slides.map((slide) => {
      let currentLatex = slide.initial_super_latex_content;
      let currentDescription = slide.initial_description_content || [];

      const processedSteps = slide.steps?.map((step) => {
        if (step.super_latex_actions) {
          currentLatex = applySuperLatexActions(currentLatex, step.super_latex_actions);
        }
        if (step.description_content_actions) {
          currentDescription = applyLearningContentActions(currentDescription, step.description_content_actions);
        }

        return {
          ...step,
          computedContent: currentLatex,
          computedDescription: currentDescription,
        };
      });

      const steps =
        processedSteps && processedSteps.length > 0
          ? processedSteps
          : [
              {
                title: slide.name || '',
                computedContent: currentLatex,
                computedDescription: currentDescription,
              },
            ];

      return { ...slide, steps };
    });
  });

  // Tracks the active step index for each slide
  // Key: slideIndex, Value: stepIndex
  readonly currentStepIndices = signal<Record<number, number>>({});

  private touchStartX = 0;
  private touchEndX = 0;

  readonly ContentBlockType = ContentBlockType;

  setExpanded(expanded: boolean): void {
    this.expanded.set(expanded);
  }

  getCurrentStepIndex(slideIndex: number): number {
    const indices = this.currentStepIndices();
    return indices[slideIndex] ?? 0;
  }

  setCurrentStepIndex(slideIndex: number, stepIndex: number): void {
    this.currentStepIndices.update((indices) => ({
      ...indices,
      [slideIndex]: stepIndex,
    }));
  }

  goToPreviousStep(slideIndex: number): void {
    const current = this.getCurrentStepIndex(slideIndex);
    if (current > 0) {
      this.setCurrentStepIndex(slideIndex, current - 1);
    }
  }

  goToNextStep(slideIndex: number, totalSteps: number): void {
    const current = this.getCurrentStepIndex(slideIndex);
    if (current < totalSteps - 1) {
      this.setCurrentStepIndex(slideIndex, current + 1);
    }
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  onTouchEnd(event: TouchEvent, slideIndex: number, totalSteps: number): void {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipe(slideIndex, totalSteps);
  }

  private handleSwipe(slideIndex: number, totalSteps: number): void {
    const swipeThreshold = 50;
    if (this.touchEndX < this.touchStartX - swipeThreshold) {
      // Swipe Left -> Next Step
      this.goToNextStep(slideIndex, totalSteps);
    }
    if (this.touchEndX > this.touchStartX + swipeThreshold) {
      // Swipe Right -> Previous Step
      this.goToPreviousStep(slideIndex);
    }
  }
}
